/**
 * Improved scroll utilities for consistent behavior across the website
 */

// Throttle function to limit how often a function can be called
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Smoothly scrolls to an element with the specified ID
 * @param {string} elementId - The ID of the element to scroll to
 * @param {Object} options - Additional options
 * @param {boolean} options.smooth - Whether to use smooth scrolling behavior (default: true)
 * @param {number} options.delay - Delay in ms before scrolling (default: 0)
 * @param {number} options.offset - Offset in pixels from the top (default: 0, to account for fixed header)
 */
export const scrollToElement = (elementId, options = {}) => {
  const { smooth = true, delay = 0, offset = 80 } = options;
  
  const scrollAction = () => {
    const element = document.getElementById(elementId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  };
  
  if (delay > 0) {
    setTimeout(scrollAction, delay);
  } else {
    scrollAction();
  }
};

/**
 * Scrolls to the top of the page
 * @param {Object} options - Additional options
 * @param {boolean} options.smooth - Whether to use smooth scrolling behavior (default: true)
 * @param {number} options.delay - Delay in ms before scrolling (default: 0)
 */
export const scrollToTop = (options = {}) => {
  const { smooth = true, delay = 0 } = options;
  
  const scrollAction = () => {
    window.scrollTo({
      top: 0,
      behavior: smooth ? 'smooth' : 'auto',
    });
  };
  
  if (delay > 0) {
    setTimeout(scrollAction, delay);
  } else {
    scrollAction();
  }
};

/**
 * Create a throttled scroll event listener
 * @param {Function} callback - The function to call on scroll
 * @param {number} delay - Throttle delay in ms (default: 100)
 */
export const createScrollListener = (callback, delay = 100) => {
  return throttle(callback, delay);
};

/**
 * Save the current scroll position with a key
 * @param {string} key - Unique key to identify this scroll position
 */
export const saveScrollPosition = (key) => {
  const scrollPositions = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');
  scrollPositions[key] = window.pageYOffset;
  sessionStorage.setItem('scrollPositions', JSON.stringify(scrollPositions));
};

/**
 * Restore a saved scroll position by key
 * @param {string} key - The key used when saving the position
 * @param {boolean} smooth - Whether to use smooth scrolling behavior
 */
export const restoreScrollPosition = (key, smooth = false) => {
  const scrollPositions = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');
  const savedPosition = scrollPositions[key];
  
  if (savedPosition !== undefined) {
    window.scrollTo({
      top: savedPosition,
      behavior: smooth ? 'smooth' : 'auto',
    });
    return true;
  }
  return false;
};
